import Link from "next/link";
import { formatPrice } from "@/app/utilities/helper";
import ImageWrapper from "@/component/ui/ImageWrapper";
import LeadButton from "@/component/ui/Lead/LeadButton";
import { PROMOTED_MODEL_TRACKING } from "@/lib/constants/tracking";
import { PROMOTED_MODEL_CTA_TEXT } from "@/utils/promotedModels";

interface PromotedModelCardProps {
    car: any;
    position: number; // 1-based slot in the list, exposed to GTM.
    isMobile?: boolean;
    wide?: boolean; // Single-row layout with the CTA on the right from 1024px up.
    onRoadPrice?: number; // On-road price in the page city; falls back to ex-showroom when absent.
    cityName?: string;
}

export default function PromotedModelCard({ car, position, isMobile = false, wide = false, onRoadPrice, cityName }: PromotedModelCardProps) {
    const title = `${car.brand_name} ${car.name}`;
    const priceValue = onRoadPrice || car.price_range?.base;
    const price = priceValue ? formatPrice(priceValue, { includeUnit: true }) : "Price on Request";
    const priceNote = priceValue && (onRoadPrice ? `On-road price${cityName ? `, ${cityName}` : ""}` : "Ex-showroom price");

    return (
        <div
            className={`flex flex-col gap-[10px] rounded-[14px] border border-[#E3E6EA] bg-white p-3 lg:rounded-[12px] ${wide ? "lg:flex-row lg:items-center lg:gap-5 lg:p-4" : "h-full"}`}
            data-model-id={car._id}
            data-position={position}
            data-testid="promoted-model-card"
        >
            <Link prefetch={false} href={`/${car.brand_slug}/${car.slug}`} title={title} className={`flex items-center gap-3 lg:flex-1 ${wide ? "lg:gap-5" : ""} ${PROMOTED_MODEL_TRACKING.CARD_CLICK}`}>
                <div className={`relative aspect-[284/152] w-[104px] shrink-0 overflow-hidden rounded-[10px] bg-[#F5F7FA] lg:rounded-[8px] ${wide ? "lg:w-[132px]" : ""}`}>
                    <ImageWrapper
                        src={car.featured_image}
                        alt={title}
                        title={title}
                        fill
                        sizes={wide ? "132px" : "104px"}
                        sourceWidth={284}
                        sourceHeight={152}
                        isMobile={isMobile}
                        className="object-contain"
                    />
                </div>
                <div className="min-w-0">
                    <p className="line-clamp-2 text-[16px] font-bold leading-[20px] text-[#101828] lg:text-[15px] lg:leading-[19px]">{title}</p>
                    <p className="mt-1 text-[15px] font-bold text-[#101828] lg:text-[14px]">
                        {price}
                        {priceValue && <span className="font-normal"> Onwards</span>}
                    </p>
                    {priceNote && <p className="mt-0.5 text-[12px] leading-[16px] text-[#5B626B]">{priceNote}</p>}
                </div>
            </Link>
            <LeadButton
                carId={car._id}
                carName={title}
                btnText={PROMOTED_MODEL_CTA_TEXT}
                fullWidth
                className={`mt-auto flex min-h-[44px] w-full cursor-pointer items-center justify-center rounded-[10px] bg-[#0B7A75] p-3 text-[15px] font-medium text-[#fff] hover:opacity-90 lg:rounded-[8px] lg:text-[14px] ${wide ? "lg:mt-0 lg:w-auto lg:shrink-0 lg:border lg:border-[#0B7A75] lg:bg-white lg:px-4 lg:text-[#0B7A75] lg:hover:bg-[#0B7A75]/5 lg:hover:opacity-100 lg:after:ml-1.5 lg:after:content-['›']" : ""} ${PROMOTED_MODEL_TRACKING.GET_OFFERS_CTA}`}
                leadsumbittracking={PROMOTED_MODEL_TRACKING.LEAD_SUBMIT}
                leadSource={PROMOTED_MODEL_TRACKING.LEAD_SOURCE}
                datatestid="car-lead-cta-button"
            />
        </div>
    );
}
